import { environment } from '@environments/environment';
import { ModuleService } from '@core/services/module.service';
import { isSuperAdmin } from '@core/authentication/super-admin.guard';
import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { UserService } from '@core/services/user.service';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { LoggedUserModel } from '@core/authentication/logged-user.model';
import { MatExpansionModule } from '@angular/material/expansion';

import { MatIconModule } from '@angular/material/icon';
import { AssociationsStore } from '@app/features/associations/store/associations.store';
import { KmdMenuLinksService } from '@app/features/associations/kmd-menu-links.service';
import { KMD_GROUPS, KMD_OWN_PAGES, KmdPage, OWN_MENU_PERMISSIONS, OwnMenuPermission } from '@app/features/associations/kmd-pages';
import { HelpStore } from '@features/help';
import { isAssociationLogin } from '@shared/utilities/association-role.util';
import { Role } from '@shared/models/enums';
import { filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./styles/main-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterModule,
    MatExpansionModule,
    MatIconModule,
    NgClass,
    NgTemplateOutlet,
  ],
})
export class MainMenuComponent implements OnInit {
  readonly associationsStore = inject(AssociationsStore);
  /** For the "Ny" badge on Hjælp: a release note newer than the last one opened in this browser. */
  readonly helpStore = inject(HelpStore);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  env = environment;

  // True on the booking-period detail page (/associations/:id/booking-period/:id),
  // so "Bookinger" (or, for an association login, Bookinger → "Overblik") stays highlighted
  // and its panel expanded there.
  readonly isBookingPeriodActive = signal(false);
  // Min forening → Brugere: granted per role in Rettigheder (view or manage). The page and the API
  // also check that the user holds that role in the selected association.
  readonly canSeeAssociationUsers = signal(false);
  // Indstillinger → Moduler: SuperAdmin only (the modules sold per anlæg).
  readonly canManageModules = signal(false);
  // Booking → Dørkoder: the doors module, when one of the user's anlæg has bought it.
  readonly hasDoorsModule = inject(ModuleService).has('doors');
  currentUserLoggedIn: LoggedUserModel;

  canManageBookings = false;
  canManageCodes = false;
  canManageUsers = false;
  canManageFacilities = false;
  canManagePrices = false;
  canManageSettings = false;
  canManageSubscriptions = false;
  canManagePermissions = false;
  canManageImport = false;
  /** Lock systems hold an address and a password abook's server uses; the API allows SuperAdmin only. */
  isSuperAdmin = false;
  canManageAssociations = false;
  canManageOthersAssociations = false; // not in use
  /**
   * An organisation's person (16/17/19–22, #797): Bookinger → Overblik, Søg/Book, the
   * own-organisation panel and the KMD pages. Kamp only when that organisation is an association
   * (`ownIsOrganisation`); which KMD pages, after the pages' `types` (`isGranted`).
   */
  readonly isAssociationLogin = signal(true);
  // Which KMD entries the menu has is decided by `KmdMenuLinksService`, which the help shares: it shows
  // its guides to the KMD pages exactly where the menu has them.
  private readonly kmdLinks = inject(KmdMenuLinksService);
  // The KMD domain without a trailing slash. Empty until `GET /kmdsettings` answers with one.
  readonly kmdDomain = this.kmdLinks.domain;
  readonly showKmdMenuLinks = this.kmdLinks.menuLinksEnabled;
  /**
   * The selected organisation is a company or an internal customer, not an association (#797): no
   * Kamp. Which KMD pages its people get is the pages' own `types` (see `isGranted`).
   */
  readonly ownIsOrganisation = this.kmdLinks.ownIsOrganisation;
  /** The selected organisation's type, or null while the user's organisations are still being loaded. */
  private readonly ownType = this.kmdLinks.ownType;
  /**
   * Startside and the KMD pages of the login's own organisation: an organisation's people, where the
   * tenant has them on.
   */
  readonly showOwnKmdLinks = computed(() => this.kmdLinks.showOwnLinks(this.isAssociationLogin()));
  /** "Min forening", "Min virksomhed" or "Min institution", after the selected organisation's type. */
  readonly ownMenuName = computed(
    () =>
      ({ [Role.Company]: 'Min virksomhed', [Role.InternalUser]: 'Min institution' })[
        this.associationsStore.selectedOrganisationTypeId() ?? 0
      ] ?? 'Min forening',
  );
  readonly showKmdPortalLink = computed(() => this.kmdLinks.showPortalLink(this.isAssociationLogin()));
  /**
   * The own-association menu permissions the session's role holds. A foreningsansvarlig holds all
   * four, a foreningsbooker none, so the booker's KMD menu is Startside, Dokumenter og Informationer
   * and the account's Grundoplysninger and Notifikationer, and Min forening has no Website tekst. A
   * company's or internal customer's booker gets the same minus Dokumenter og Informationer
   * (`types`). Empty until `can_i` answers.
   */
  private readonly ownMenuPermissions = signal<ReadonlySet<OwnMenuPermission>>(new Set());
  /** KMD has the page for the selected organisation's type, and the session's role holds its permission. */
  private readonly isGranted = (page: KmdPage) => {
    const type = this.ownType();
    return (
      (!page.types || (type !== null && page.types.includes(type))) &&
      (!page.permission || this.ownMenuPermissions().has(page.permission))
    );
  };
  /** The selected organisation's own pages in KMD (Min forening / Min virksomhed), after its type. */
  readonly kmdAssociationPages = computed(() => KMD_OWN_PAGES.filter(this.isGranted));
  /** Min forening → Website tekst, for every organisation type. Administrators' Foreninger panel shows it regardless. */
  readonly showOwnWebsiteText = computed(() => this.ownMenuPermissions().has('view_association_website_text'));
  /** The own-organisation panel, only when it holds something: no empty panel for a booker. */
  readonly showOwnPanel = computed(
    () =>
      this.canSeeAssociationUsers() ||
      this.showOwnWebsiteText() ||
      (this.showOwnKmdLinks() && this.kmdAssociationPages().length > 0),
  );
  readonly kmdGroups = computed(() =>
    KMD_GROUPS.map((group) => ({ ...group, pages: group.pages.filter(this.isGranted) }))
      .filter((group) => group.pages.length > 0),
  );
  // `?CustomerId=` for the KMD page links: the selected association's customer id in KMD (the
  // KmdCustomerId of its own customer, #1348). Left off when the association has none.
  readonly kmdCustomerQuery = computed(() => {
    const customerId = this.associationsStore.selectedAssociationKmdCustomerId();
    return customerId ? `?CustomerId=${customerId}` : '';
  });

  constructor() {
    this.authenticationService.loggedUserSubject
      .pipe(
        // The subject is seeded with `null` and stays there until `setUser()` resolves, on a
        // different async chain than the guard that let us in here. Reading that seed would
        // answer "not an association" for everyone and flash the Dashboard/Bookinger entry into an
        // association login's menu, so the safe default above stands until the user is known.
        filter((loggedUser) => loggedUser?.primaryRole !== undefined),
        takeUntilDestroyed(),
      )
      .subscribe((loggedUser) => {
        this.currentUserLoggedIn = loggedUser;
        // Kept in step with the user rather than resolved once in ngOnInit: it gates the
        // Dashboard/Bookinger entry — the landing page — which must not stay hidden behind the eleven
        // `can_i` round trips below.
        this.isAssociationLogin.set(isAssociationLogin(loggedUser?.primaryRole));
        this.canManageModules.set(isSuperAdmin(loggedUser));
      });

    this.updateBookingPeriodActive();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.updateBookingPeriodActive());
  }

  private updateBookingPeriodActive(): void {
    this.isBookingPeriodActive.set(this.router.url.includes('/booking-period/'));
  }

  async ngOnInit() {
    // Before the `can_i` round trips below, not after: it depends on none of them, and an
    // association login's KMD links wait for it.
    void this.kmdLinks.load();
    // Also first: they decide the whole own-organisation panel of an association login, which
    // would otherwise pop in only after the administrator round trips below.
    const [canViewAssociationUsers, canManageAssociationUsers, ownMenuGrants] = await Promise.all([
      firstValueFrom(this.userService.canI('view_association_users')),
      firstValueFrom(this.userService.canI('manage_association_users')),
      Promise.all(
        OWN_MENU_PERMISSIONS.map(async (permission) =>
          (await firstValueFrom(this.userService.canI(permission))) ? permission : null,
        ),
      ),
    ]);
    this.ownMenuPermissions.set(new Set(ownMenuGrants.filter((permission) => permission !== null)));
    this.canSeeAssociationUsers.set(canViewAssociationUsers || canManageAssociationUsers);
    this.canManageBookings = await firstValueFrom(this.userService.canI('manage_bookings'));
    this.canManageCodes = await firstValueFrom(this.userService.canI('manage_codes'));
    this.canManageUsers = await firstValueFrom(this.userService.canI('manage_users'));
    this.canManageFacilities = await firstValueFrom(this.userService.canI('manage_facilities'));
    this.canManagePrices = await firstValueFrom(this.userService.canI('manage_schedules'));
    this.canManageSettings = await firstValueFrom(this.userService.canI('manage_settings'));
    this.canManageImport = await firstValueFrom(this.userService.canI('manage_import'));
    // Rettigheder is SuperAdmin-only (#924), a role check rather than a capability.
    this.canManagePermissions = await firstValueFrom(this.authenticationService.isSuperAdmin());
    this.canManageAssociations = await firstValueFrom(this.userService.canI('manage_associations'));
    this.canManageOthersAssociations = await firstValueFrom(
      this.userService.canI('manage_others_associations'),
    );
    this.canManageSubscriptions = await firstValueFrom(
      this.userService.canI('manage_subscriptions'),
    );
    this.isSuperAdmin = isSuperAdmin(this.currentUserLoggedIn);
  }
}
